import {
  OrthographicCamera as OrthographicCameraThree,
  PerspectiveCamera as PerspectiveCameraThree,
} from 'three'
import type { CameraControls } from '@react-three/drei'

export interface StatikE2ECameraBridge {
  canvasZoomBySteps: (steps: number) => Promise<void>
  /** Project a model point to canvas CSS pixels (origin: canvas top-left). */
  projectModelPoint: (x: number, y: number, z: number) => { x: number; y: number }
  /** Current camera look-at target in model metres. */
  getTarget: () => { x: number; y: number; z: number }
}

/** Same multiplier the old Cypress `wheel` command used (`deltaY = steps * this`). */
export const LEGACY_CANVAS_ZOOM_DELTA_Y_SCALE = -14

/**
 * Normalized scroll delta as in camera-controls `onMouseWheel` for DOM_DELTA_PIXEL
 * (the mode synthetic Cypress `wheel` events effectively used).
 */
export function legacyCanvasZoomNormalizedDelta(steps: number): number {
  const deltaY = steps * LEGACY_CANVAS_ZOOM_DELTA_Y_SCALE
  // Deterministic E2E behavior: camera-controls applies platform-specific scaling
  // (macOS vs others) for real input devices. In Cypress we want the *same* zoom
  // for a given `steps` value on both macOS and Linux CI, so we lock to the
  // macOS normalization factor.
  const deltaYFactor = -1
  return deltaY / (deltaYFactor * 10)
}

/**
 * Applies one "legacy canvasZoom(steps)" impulse using the same scaling as
 * camera-controls wheel handling (`_zoomInternal` / `_dollyInternal`), so results
 * match across macOS vs Linux CI for a given `steps` value.
 */
export async function applyLegacyCanvasZoomSteps(
  controls: CameraControls,
  camera: OrthographicCameraThree | PerspectiveCameraThree,
  steps: number,
): Promise<void> {
  const delta = legacyCanvasZoomNormalizedDelta(steps)
  const wheelArg = -delta

  if (camera instanceof OrthographicCameraThree) {
    const zoomScale = Math.pow(0.95, wheelArg * controls.dollySpeed)
    await controls.zoomTo(camera.zoom * zoomScale, false)
  } else {
    const dollyScale = Math.pow(0.95, -wheelArg * controls.dollySpeed)
    await controls.dollyTo(controls.distance * dollyScale, false)
  }
}
